import { FC, useContext, useEffect, useState } from 'react';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import { RouteComponentProps } from 'react-router';
import { Link, withRouter } from 'react-router-dom';
import * as Yup from 'yup';
import { Box, Button, Flex, StyledForm, StyledInput } from '../../../../components/shared';
import { Label, Span } from '../../../../typography';
import { AuthContext } from '../AuthContext';
import { SCREEN } from '../../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

type LoginFormValues = {
  email: string;
  password: string;
};

type LoginFormProps = RouteComponentProps & {
  initialValues?: object;
  redirectTo?: string;
};

const LoginValidationSchema = Yup.object().shape({
  email: Yup.string().required('Required'),
  password: Yup.string().required('Required'),
});

const LoginForm: FC<LoginFormProps> = ({ history, redirectTo }) => {
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const { authenticated, defaultRedirectRoute, login } = useContext(AuthContext);

  useEffect(() => {
    if (authenticated && defaultRedirectRoute) {
      history.push(redirectTo || defaultRedirectRoute);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, defaultRedirectRoute]);

  return (
    <Formik
      validationSchema={LoginValidationSchema}
      initialValues={{ email: '', password: '' }}
      onSubmit={async (values: LoginFormValues, { setStatus, setSubmitting }) => {
        const data = Object.assign({}, values);
        try {
          setSubmitting(true);
          await login(data);
        } catch (e) {
          setStatus((e as any).message);
          setSubmitting(false);
        }
      }}
      render={({ status, isSubmitting }) => {
        return (
          <StyledForm>
            <Field
              name="email"
              render={({ field }: FieldProps<LoginFormValues>) => (
                <Box width="100%" mb={3}>
                  {!isWeb && (
                    <Label
                      style={{
                        fontSize: '0.8em',
                        fontWeight: 100,
                        letterSpacing: '1.1px',
                        marginBottom: '5px',
                      }}
                    >
                      Email Address
                    </Label>
                  )}
                  <StyledInput type="email" px={2} {...field} placeholder={isWeb ? 'Enter email' : ''} />
                  <ErrorMessage name="email">
                    {msg => (
                      <Span mt={2} color="error" textAlign="right">
                        {msg}
                      </Span>
                    )}
                  </ErrorMessage>
                </Box>
              )}
            />
            <Field
              name="password"
              render={({ field }: FieldProps<LoginFormValues>) => (
                <Box width="100%" mb={3}>
                  {!isWeb && <Label>Password</Label>}
                  <StyledInput
                    px={2}
                    {...field}
                    showEyeIcon={true}
                    placeholder={isWeb ? 'Password' : ''}
                    passwordVisible={showPassword}
                    type={showPassword ? 'text' : 'password'}
                    onTogglePassword={() => setShowPassword(!showPassword)}
                  />
                  <ErrorMessage name="password">
                    {msg => (
                      <Span mt={2} color="error" textAlign="right">
                        {msg}
                      </Span>
                    )}
                  </ErrorMessage>
                </Box>
              )}
            />

            <Flex
              direction="row"
              justify="flex-start"
              size="1"
              style={{ width: '100%', padding: '10px 0', position: 'relative', top: '-15px' }}
            >
              <Link to="/forgot-password" style={{ fontSize: '0.8em', color: '#808080' }}>
                Forgot your password?
              </Link>
            </Flex>

            {status && (
              <Span width="100%" textAlign="center" color="error" mb={3}>
                {status.replace('GraphQL error:', '').trim()}
              </Span>
            )}
            <Button type="submit" text="LOGIN" isLoading={isSubmitting} disabled={isSubmitting} isWeb={isWeb} />
          </StyledForm>
        );
      }}
    />
  );
};

export default withRouter(LoginForm);
